import React from 'react';
import { connect } from 'react-redux';
import { Autocomplete, Box, Button, MenuItem, SxProps, TextField, Theme } from '@mui/material';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import dayjs, { Dayjs } from 'dayjs';

import { sortBy } from '@paragon/utilities';

import { displayNames, PropertyKey, propertyKeys } from '../../../propertyMetadata';
import { getSuggestions } from '../../../selectors/getSuggestions';
import { RootState } from '../../../store';
import { DoubleOperator, JobFilter, Operator, SingleOperator } from '../../../store/viewsSlice';

interface ColumnsDropdownProps {
  onChange: (column: PropertyKey) => void;
  selectedColumn: PropertyKey | null;
  sx: SxProps<Theme>;
}

function ColumnsDropdown(props: ColumnsDropdownProps) {
  const columns = sortBy(propertyKeys, (propertyKey) => displayNames[propertyKey]).map((propertyKey) => (
    <MenuItem key={propertyKey} value={propertyKey}>
      {displayNames[propertyKey]}
    </MenuItem>
  ));
  return (
    <TextField
      id="outlined-select-filter"
      sx={props.sx}
      select
      label="Add New"
      value={props.selectedColumn ?? ''}
      onChange={(event) => {
        props.onChange(event.target.value as PropertyKey);
      }}
      helperText="Please select your filter"
      variant="outlined"
    >
      {columns}
    </TextField>
  );
}

interface StringInputProps {
  value: string;
  onChange: (value: string) => void;
  suggestions: string[];
}

function StringInput(props: StringInputProps) {
  const { suggestions, value, onChange } = props;

  return (
    <Box sx={{ width: 300 }}>
      <Autocomplete
        freeSolo
        disableClearable
        fullWidth
        options={suggestions}
        renderInput={(params) => <TextField {...params} />}
        value={value}
        onChange={(event, newValue) => {
          onChange(newValue);
        }}
      />
    </Box>
  );
}

interface NumberInputProps {
  onChange: (value: string) => void;
  value: string;
}

function NumberInput(props: NumberInputProps) {
  return <TextField type="number" value={props.value} onChange={(e) => props.onChange(e.currentTarget.value)} />;
}

interface DateInputProps {
  onChange: (string: string) => void;
  value: string;
}

function DateInput(props: DateInputProps) {
  return (
    <LocalizationProvider dateAdapter={AdapterDayjs}>
      <DatePicker
        value={props.value === '' ? null : dayjs(props.value)}
        onChange={(date) => {
          if (date) props.onChange(formatDate(date));
        }}
      />
    </LocalizationProvider>
  );
}

const formatDate = (date: Dayjs) => `${date.month() + 1}/${date.date()}/${date.year()}`;

interface OperatorAndDisplayName {
  readonly operator: Operator;
  readonly displayName: string;
}

const stringOperatorsAndDisplayNames: readonly OperatorAndDisplayName[] = [
  { operator: '=', displayName: 'equals' },
  { operator: '!=', displayName: 'does not equal' },
];

const stringPropertyKeys = [
  'name',
  'regions',
  'jobType',
  'creator',
  'assignedTo',
  'openedBy',
  'organizationOwnedBy',
  'jobStatus',
  'jobPriority',
  'sharedWithOrganizations',
  'billingLocation',
] as const;
type StringPropertyKey = (typeof stringPropertyKeys)[number];

type StringOperator = '=' | '!=';
interface StringJobFilter {
  readonly propertyKey: StringPropertyKey;
  readonly operator: StringOperator;
  readonly value: string;
}

type SingleNumberOperator = '=' | '<' | '>' | '<=' | '>=';
type DoubleNumberOperator = '>= <=';

const numberOperatorsAndDisplayNames: readonly OperatorAndDisplayName[] = [
  { operator: '=', displayName: 'equals' },
  { operator: '<', displayName: 'less than' },
  { operator: '>', displayName: 'greater than' },
  { operator: '<=', displayName: 'less than or equal to' },
  { operator: '>=', displayName: 'greater than or equal to' },
  { operator: '>= <=', displayName: 'between' },
];

const numberPropertyKeys = ['id', 'numberOfDesigns'] as const;
type NumberPropertyKey = (typeof numberPropertyKeys)[number];

interface SingleOperatorNumberJobFilter {
  readonly propertyKey: NumberPropertyKey;
  readonly operator: SingleNumberOperator;
  readonly value: string;
}
interface DoubleOperatorNumberJobFilter {
  readonly propertyKey: NumberPropertyKey;
  readonly operator: DoubleNumberOperator;
  readonly low: string;
  readonly high: string;
}
type NumberJobFilter = SingleOperatorNumberJobFilter | DoubleOperatorNumberJobFilter;

type SingleDateOperator = '=' | '<=' | '>=';
type DoubleDateOperator = '>= <=';

const dateOperatorsAndDisplayNames: readonly OperatorAndDisplayName[] = [
  { operator: '=', displayName: 'equals' },
  { operator: '<=', displayName: 'before and including' },
  { operator: '>=', displayName: 'after and including' },
  { operator: '>= <=', displayName: 'between' },
];

const datePropertyKeys = ['createdTimestamp'] as const;
type DatePropertyKey = (typeof datePropertyKeys)[number];

interface SingleOperatorDateJobFilter {
  readonly propertyKey: DatePropertyKey;
  readonly operator: SingleDateOperator;
  readonly value: string;
}
interface DoubleOperatorDateJobFilter {
  readonly propertyKey: DatePropertyKey;
  readonly operator: DoubleDateOperator;
  readonly low: string;
  readonly high: string;
}
type DateJobFilter = SingleOperatorDateJobFilter | DoubleOperatorDateJobFilter;

function isJobFilterWithSetPropertyKey(jobFilter: DraftJobFilter): jobFilter is JobFilter {
  return jobFilter.propertyKey != null;
}
function isStringJobFilter(jobFilter: JobFilter): jobFilter is StringJobFilter {
  return (stringPropertyKeys as readonly string[]).includes(jobFilter.propertyKey);
}
function isNumberJobFilter(jobFilter: JobFilter): jobFilter is NumberJobFilter {
  return (numberPropertyKeys as readonly string[]).includes(jobFilter.propertyKey);
}
function isDateJobFilter(jobFilter: JobFilter): jobFilter is DateJobFilter {
  return (datePropertyKeys as readonly string[]).includes(jobFilter.propertyKey);
}

interface DraftSingleOperatorJobFilter {
  readonly propertyKey: PropertyKey | null;
  readonly operator: SingleOperator;
  readonly value: string;
}
interface DraftDoubleOperatorJobFilter {
  readonly propertyKey: PropertyKey | null;
  readonly operator: DoubleOperator;
  readonly low: string;
  readonly high: string;
}
export type DraftJobFilter = DraftSingleOperatorJobFilter | DraftDoubleOperatorJobFilter;

type StateProps = ReturnType<typeof mapStateToProps>;
interface OwnProps {
  jobFilter: DraftJobFilter;
  onChange: (updatedJobFilter: DraftJobFilter) => void;
  onRemove: () => void;
}
type Props = StateProps & OwnProps;

class UnconnectedJobFilterComponent extends React.Component<Props> {
  onCloseClick = () => {
    this.props.onRemove();
  };

  onChangeColumn = (column: PropertyKey) => {
    this.props.onChange({ propertyKey: column, operator: '=', value: '' });
  };

  onChangeOperator: React.ChangeEventHandler<HTMLInputElement> = (event) => {
    const operator = event.target.value as Operator;
    if (operator === '>= <=') {
      if (this.props.jobFilter.operator === '>= <=') {
        this.props.onChange({ ...this.props.jobFilter, operator });
      } else {
        this.props.onChange({ ...this.props.jobFilter, operator, low: '', high: '' });
      }
    } else if (this.props.jobFilter.operator === '>= <=') {
      this.props.onChange({ ...this.props.jobFilter, operator, value: '' });
    } else {
      this.props.onChange({ ...this.props.jobFilter, operator });
    }
  };

  onChangeValue = (value: string) => {
    if (this.props.jobFilter.operator === '>= <=') return;
    this.props.onChange({ ...this.props.jobFilter, value });
  };

  onChangeLow = (low: string) => {
    if (this.props.jobFilter.operator !== '>= <=') return;
    this.props.onChange({ ...this.props.jobFilter, low });
  };

  onChangeHigh = (high: string) => {
    if (this.props.jobFilter.operator !== '>= <=') return;
    this.props.onChange({ ...this.props.jobFilter, high });
  };

  getOperatorOptions() {
    const { jobFilter } = this.props;
    if (!isJobFilterWithSetPropertyKey(jobFilter)) return [];
    if (isStringJobFilter(jobFilter)) return stringOperatorsAndDisplayNames;
    if (isNumberJobFilter(jobFilter)) return numberOperatorsAndDisplayNames;
    if (isDateJobFilter(jobFilter)) return dateOperatorsAndDisplayNames;
    return [];
  }

  renderInputs() {
    const { jobFilter } = this.props;
    if (!isJobFilterWithSetPropertyKey(jobFilter)) return null;
    if (isStringJobFilter(jobFilter)) {
      return (
        <StringInput
          value={jobFilter.value}
          onChange={this.onChangeValue}
          suggestions={this.props.suggestions[jobFilter.propertyKey]}
        />
      );
    }
    if (isNumberJobFilter(jobFilter)) {
      return jobFilter.operator === '>= <=' ? (
        <>
          <NumberInput value={jobFilter.low} onChange={this.onChangeLow} />
          <div />
          <NumberInput value={jobFilter.high} onChange={this.onChangeHigh} />
        </>
      ) : (
        <NumberInput value={jobFilter.value} onChange={this.onChangeValue} />
      );
    }
    if (isDateJobFilter(jobFilter)) {
      return jobFilter.operator === '>= <=' ? (
        <>
          <DateInput value={jobFilter.low} onChange={this.onChangeLow} />
          <div />
          <DateInput value={jobFilter.high} onChange={this.onChangeHigh} />
        </>
      ) : (
        <DateInput value={jobFilter.value} onChange={this.onChangeValue} />
      );
    }
    return null;
  }

  renderParameters() {
    if (!this.props.jobFilter.propertyKey) {
      return <div />;
    }

    return (
      <Box sx={{ display: 'flex', flexWrap: 'wrap', justifyContent: 'center', m: 2 }}>
        <TextField
          id="outlined-select-parameter"
          select
          value={this.props.jobFilter.operator}
          onChange={this.onChangeOperator}
          variant="outlined"
        >
          {this.getOperatorOptions().map(({ operator, displayName }) => (
            <MenuItem key={operator} value={operator}>
              {displayName}
            </MenuItem>
          ))}
        </TextField>
        <Box sx={{ mx: 2, display: 'flex', flexDirection: 'column' }}>{this.renderInputs()}</Box>
        <Button variant="outlined" onClick={this.onCloseClick} sx={{ height: 56, color: '#f44336' }}>
          Cancel
        </Button>
      </Box>
    );
  }

  render() {
    return (
      <Box sx={{ display: 'flex', flexWrap: 'wrap', justifyContent: 'center', m: 2 }}>
        <ColumnsDropdown
          sx={{ m: 2 }}
          onChange={this.onChangeColumn}
          selectedColumn={this.props.jobFilter.propertyKey}
        />
        {this.renderParameters()}
      </Box>
    );
  }
}

const mapStateToProps = (state: RootState) => ({
  suggestions: getSuggestions(state),
});

export default connect(mapStateToProps)(UnconnectedJobFilterComponent);
